import { useEffect } from 'react';
import { Audience } from 'shared/models/auth';
import routes from 'config/developerSite/routes';
import DevsiteCookies, { getCookie } from 'modules/common/DevsiteCookies';
import useService from 'modules/common/hooks/useService';
import { Executor } from 'modules/common/requests/Definitions';
import { DevToolsServerExecutor } from 'modules/common/requests/DevToolsServerRequestExecutor';
import {
  AppBuilderEndpoint,
  DevsiteApiExecutor,
  OctoPaasEndpoint,
  OctopaasExecutor,
  OrchestrationBuilderEndpoint,
  OrchestrationBuilderExecutor,
} from 'modules/common/requests/ExecutorFactory';
import DevsiteEnv from '../DevsiteEnv';
import { executeLogoutCleanup } from '../utils/devsite';
import { isHttpOnlyCookieAuth, shouldUseGatewayAuth } from './authMode';
import { session } from './session';
import { getToken } from './token';

const Logout = () => {
  const octopaas = useService(OctopaasExecutor);
  const devsiteApi = useService(DevsiteApiExecutor);
  const devtoolsApi = useService(DevToolsServerExecutor);
  const fbApi = useService(OrchestrationBuilderExecutor);

  const logoutUser = async () => {
    let audience: Audience | undefined;
    try {
      const sessionInfo = await session.getInfo();
      audience = sessionInfo.platform;
    } catch (error) {
      // If session info unavailable, proceed with logout using default redirect
      audience = undefined;
    }

    const { octopaasLogout, devsiteLogout } = startSessionLogout(audience, octopaas.executor, devsiteApi.executor);

    // Logout from devtools server if devtools XSRF token is present implying an actual devtools server session was created earlier
    const devtoolsXsrfToken = getCookie(DevsiteCookies.AB_XSRF_TOKEN);
    const devtools = devtoolsXsrfToken && devtoolsApi.executor.get(AppBuilderEndpoint.LOGOUT);

    // Logout from OB/flowbuilder server if XSRF token is present
    const fbXsrfToken = getCookie(DevsiteCookies.FB_XSRF_TOKEN);
    // needs to be a post but with no body
    const fb = fbXsrfToken && fbApi.executor.post(OrchestrationBuilderEndpoint.LOGOUT, null);

    try {
      await Promise.all([octopaasLogout, devsiteLogout, devtools, fb]);
    } catch {
      // A failed revoke must not leave the user on the logout page.
    } finally {
      executeLogoutCleanup();
      window.location.assign(routes.logoutRedirect(audience));
    }
  };

  useEffect(() => {
    logoutUser();
  }, []);

  return null;
};

export const useForceLogout = () => {
  const octopaas = useService(OctopaasExecutor);
  const devsiteApi = useService(DevsiteApiExecutor);
  const devtoolsApi = useService(DevToolsServerExecutor);

  return async () => {
    let platform: Audience | undefined;
    try {
      const sessionInfo = await session.getInfo();
      platform = sessionInfo.platform;
    } catch {
      platform = undefined;
    }

    const { octopaasLogout, devsiteLogout } = startSessionLogout(platform, octopaas.executor, devsiteApi.executor);

    // Logout from devtools server if devtools XSRF token is present implying an actual devtools server session was created earlier
    const devtoolsXsrfToken = getCookie(DevsiteCookies.AB_XSRF_TOKEN);
    const devtools = devtoolsXsrfToken && devtoolsApi.executor.get(AppBuilderEndpoint.LOGOUT);

    try {
      await Promise.all([octopaasLogout, devsiteLogout, devtools]);
    } catch {
      // Session-inactivity logout still navigates after this returns.
    } finally {
      executeLogoutCleanup();
    }
  };
};

/**
 * Enterprise and BFF sessions revoke Octopaas from the browser, which can attach `Authorization: ID`.
 * ODP gateway sessions do not. That Robot Gateway call 401s when the token is not on the header.
 * Developer Site logout revokes those sessions from the cookie whenever gateway auth is on, including
 * when session info did not return an audience and the token is not readable.
 */
function startSessionLogout(platform: Audience | undefined, octopaas: Executor<string>, devsiteApi: Executor<string>) {
  const gatewayAuth = shouldUseGatewayAuth(platform) || isUnscopedOpaqueGatewaySession(platform);
  const octopaasLogout = !gatewayAuth && octopaas.post(OctoPaasEndpoint.LOGOUT, null);
  const devsiteLogout = (gatewayAuth || isHttpOnlyCookieAuth(platform)) && devsiteApi.post('/v1/logout', null);
  return { octopaasLogout, devsiteLogout };
}

function isUnscopedOpaqueGatewaySession(platform: Audience | undefined): boolean {
  return (
    platform === undefined && DevsiteEnv.GATEWAY_ROUTED_API_ENABLED && DevsiteEnv.HTTP_ONLY_AUTH_ENABLED && !getToken()
  );
}

export default Logout;
